import { LikeRender } from "../like/like-render";
import { LikeForm } from "../like/like-form";
import { phpPostData } from "../core/php-data-access";
import { LoadingText } from "../constants/LoadingText";
import commonUtil from "../util/common-utils";
import { toNonNegativeIntOrNull, toPositiveIntOrNull } from "../util/id-utils";
import { BoardMediaPlaceholderRender } from "../util/placeholders/board-media-placeholder-render";
import {
    renderParentSnsLinksData,
    renderParentContentWithHiddenHtml,
    renderParentBoardImagesPreview,
    renderParentBoardVideoPreview,
    renderParentBoardMediaPlaceholder,
    removeParentBoardVideoPreview,
} from "./modal-render";
import type { ModalPostData, ModalPostValue, ModalSnsLink } from "./modal-render";

import { ModalForm } from "./modal-form";

type ModalParentSyncData = {
    board_id?: string | null;
    post_id?: number | null;
    user_id?: number | null;
    content_images?: string[] | null;
    attached_images?: string[] | null;
    attached_videos?: string[] | null;
    comments?: Record<string, CommentLikeState> | null;
    sns_links?: ModalSnsLink[] | null;
    post_product_id?: number | null;
    reads_count?: number;
    [key: string]: ModalPostValue | undefined;
};

type CommentLikeState = {
    is_liked: boolean;
    likes_count: number;
};

export class ModalStateHandler {
    public currentPostId: number | null;

    public currentBoardId: string | null;

    private readonly observers: Map<string, MutationObserver>;

    private readonly likeForm: LikeForm;

    private readonly likeRender: LikeRender;

    private readonly modalForm: ModalForm;

    public constructor() {
        this.currentPostId = null;
        this.currentBoardId = null;
        this.observers = new Map();
        this.likeForm = new LikeForm();
        this.likeRender = new LikeRender(this.likeForm);
        this.modalForm = new ModalForm(this.likeForm);
    }

    public isEditModeActive(modal: HTMLElement | null | undefined): boolean {
        if (!modal) return false;

        if (modal.dataset.editing === "1") {
            return true;
        }

        const editForm = this.modalForm.getVisiblePostEditForm(modal);
        return Boolean(editForm && editForm.style.display !== "none");
    }

    public setCurrentPost(postId: number, boardId: string): void {
        this.currentPostId = postId;
        this.currentBoardId = boardId;
    }

    public clearCurrentPost(): void {
        this.currentPostId = null;
        this.currentBoardId = null;
        this.cleanupObservers();
    }

    public setupCountUpdateListeners(modalBody: HTMLElement | null | undefined): void {
        if (!modalBody) return;

        this.cleanupObservers();
    }

    public setupLikeCountObserver(modalBody: HTMLElement): void {
        const likeCountElement = this.modalForm.getPostLikeCountElement(modalBody);
        if (!likeCountElement) return;

        const observer = new MutationObserver(() => {
            const likeCount = toNonNegativeIntOrNull(likeCountElement.textContent);
            if (this.currentPostId && likeCount !== null) {
                this.updateParentLikeCount(this.currentPostId, likeCount);
            }
        });

        observer.observe(likeCountElement, {
            childList: true,
            characterData: true,
            subtree: true,
        });

        this.observers.set("like", observer);
    }

    public setupCommentCountObserver(modalBody: HTMLElement): void {
        const commentCountElements = this.modalForm.getCommentCountElements(modalBody);

        commentCountElements.forEach((element, index) => {
            const observer = new MutationObserver(() => {
                const newCount = toNonNegativeIntOrNull(element.textContent);
                if (this.currentPostId && newCount !== null) {
                    this.updateParentCommentCount(this.currentPostId, newCount);
                }
            });

            observer.observe(element, {
                childList: true,
                characterData: true,
                subtree: true,
            });

            this.observers.set(`comment-${index}`, observer);
        });
    }

    public cleanupObservers(): void {
        this.observers.forEach((observer) => observer.disconnect());
        this.observers.clear();
    }

    public updateReadCount(modalBody: HTMLElement | null | undefined, count: number): void {
        if (!modalBody) return;

        const readBtn = this.modalForm.getReadCountElement(modalBody);
        if (readBtn) {
            readBtn.textContent = String(count);
        }
    }

    private withParentPostCard(postId: number, updater: (postCard: HTMLElement) => void): void {
        const postCard = this.findParentPostCard(postId);
        if (!postCard) {
            return;
        }

        updater(postCard);
    }

    private updateLikeButtonState(likeButton: HTMLElement, isLiked: boolean): void {
        likeButton.classList.toggle("liked", isLiked);

        const icon = this.likeForm.getIconElement(likeButton);
        if (icon) {
            const iconLiked = "bi-heart-fill";
            const iconUnliked = "bi-heart";
            icon.className = `bi ${isLiked ? iconLiked : iconUnliked}`;
        }
    }

    public updateParentReadCount(postId: number, count: number): void {
        this.withParentPostCard(postId, (postCard) => {
            const readDisplay = this.modalForm.getReadCountElement(postCard);
            if (readDisplay) {
                readDisplay.textContent = String(count);
            }
        });
    }

    public updateParentLikeCount(postId: number, count: number): void {
        this.withParentPostCard(postId, (postCard) => {
            const likeCount = this.modalForm.getPostLikeCountElement(postCard);
            if (likeCount) {
                likeCount.textContent = String(count);
            }
        });
    }

    public updateParentCommentCount(postId: number, count: number): void {
        this.withParentPostCard(postId, (postCard) => {
            const commentCount = this.modalForm.getCommentActionCountElement(postCard);
            if (commentCount) {
                commentCount.textContent = String(count);
            }
        });
    }

    public updateParentLikeState(postId: number, isLiked: boolean): void {
        this.withParentPostCard(postId, (postCard) => {
            const likeBtn = this.modalForm.getPostLikeButton(postCard);
            if (likeBtn) {
                this.updateLikeButtonState(likeBtn, isLiked);
            }
        });
    }

    public syncFromParent(postId: number): ModalPostData | null {
        const postData = phpPostData(postId) as ModalPostData | null;
        if (!postData) {
            console.warn("[syncFromParent] No parent post data found", { postId });
            return null;
        }

        return postData;
    }

    public syncToParent(
        postId: number,
        action: string,
        key: string,
        value: ModalPostValue | undefined,
        postData: ModalParentSyncData | null = null,
    ): void {
        if (!Number.isInteger(postId) || postId <= 0) return;

        const postCard = this.findParentPostCard(postId);
        if (!postCard) return;

        if (!postData) {
            postData = phpPostData(postId) as ModalParentSyncData | null;
        }

        if (postData) {
            if (action !== "delete") {
                postData[key] = value;
            }
        }

        switch (key) {
            case "post_title":
                this.updateParentTitle(postCard, String(value ?? ""));
                break;
            case "content":
                this.updateParentContent(postCard, String(value ?? ""));
                break;
            case "sns_links":
                this.updateParentSnsLinks(postCard, Array.isArray(value) ? (value as ModalSnsLink[]) : null);
                break;
            case "attached_images":
                this.updateParentAttachedImages(postCard, Array.isArray(value) ? (value as string[]) : null, postData);
                break;
            case "attached_videos":
                this.updateParentAttachedVideos(postCard, Array.isArray(value) ? (value as string[]) : null, postData);
                break;
            case "likes_count":
                if (toNonNegativeIntOrNull(value) !== null) {
                    this.updateParentLikeCount(postId, toNonNegativeIntOrNull(value) as number);
                }
                break;
            case "comments_count":
                if (toNonNegativeIntOrNull(value) !== null) {
                    this.updateParentCommentCount(postId, toNonNegativeIntOrNull(value) as number);
                }
                break;
            case "reads_count":
                if (toNonNegativeIntOrNull(value) !== null) {
                    this.updateParentReadCount(postId, toNonNegativeIntOrNull(value) as number);
                }
                break;
            case "is_liked":
            case "is_liked_by_current_user":
                this.updateParentLikeState(postId, Boolean(value));
                break;
        }
    }

    public findParentPostCard(postId: number): HTMLElement | null {
        return this.modalForm.findParentPostCard(postId);
    }

    private resolveParentPostId(postCard: HTMLElement, postData: ModalParentSyncData | null): number | null {
        return toPositiveIntOrNull(postData?.post_id ?? postCard.dataset.postId);
    }

    private resolveParentUserId(postCard: HTMLElement, postData: ModalParentSyncData | null): number | null {
        return toPositiveIntOrNull(postData?.user_id ?? postCard.dataset.userId);
    }

    public updateParentAttachedImages(
        postCard: HTMLElement,
        attachedImages: string[] | null | undefined,
        postData: ModalParentSyncData | null,
    ): void {
        const mediaArea = this.modalForm.getBoardMediaArea(postCard);
        if (!mediaArea) return;

        const boardId = postCard.dataset.boardId || postData?.board_id;
        const postId = this.resolveParentPostId(postCard, postData);

        const attached = Array.isArray(attachedImages) ? attachedImages.filter(Boolean) : [];
        const contentImages = Array.isArray(postData?.content_images) ? postData.content_images.filter(Boolean) : [];
        const allImages = Array.from(new Set([...(attached || []), ...(contentImages || [])]));
        const hasVideo = Array.isArray(postData?.attached_videos) && postData.attached_videos.length > 0;

        if (allImages.length > 0) {
            const displayImages = allImages.slice(0, 4) as string[];
            const userId = this.resolveParentUserId(postCard, postData);
            renderParentBoardImagesPreview(mediaArea, {
                images: displayImages,
                totalImages: allImages.length,
                boardId,
                userId,
            });

            if (hasVideo) {
                this.updateParentAttachedVideos(postCard, postData?.attached_videos, postData);
            }

            return;
        }

        this.modalForm.getBoardImagesGrid(mediaArea)?.remove();
        if (hasVideo) {
            this.modalForm.getBoardMediaPlaceholder(mediaArea)?.remove();
            this.updateParentAttachedVideos(postCard, postData?.attached_videos, postData);
        } else {
            this.modalForm.getBoardVideoWrap(mediaArea)?.remove();
            // If OGP/SNS preview is already in the media area, keep it as-is.
            // renderParentBoardMediaPlaceholder would clear innerHTML and lose the OGP preview.
            const hasOgpOrSns = this.modalForm.getOgpOrSnsPreview(mediaArea) instanceof HTMLElement;
            if (!this.modalForm.getBoardMediaPlaceholder(mediaArea) && !hasOgpOrSns) {
                const placeholder = BoardMediaPlaceholderRender.createLoadingPlaceholder({
                    postId,
                    loadingText: LoadingText.LOADING,
                });
                renderParentBoardMediaPlaceholder(mediaArea, placeholder);
                // SSR sets roleAdPlaceholderTarget="0" when its own ad is pre-rendered.
                // After modal save recreates the loading placeholder (no SSR ad in DOM),
                // delete the attribute so isPlaceholderTarget() falls through to !hasAnyMedia().
                delete postCard.dataset.roleAdPlaceholderTarget;
            }
        }
    }

    public updateParentAttachedVideos(
        postCard: HTMLElement,
        attachedVideos: string[] | null | undefined,
        postData: ModalParentSyncData | null,
    ): void {
        const mediaArea = this.modalForm.getBoardMediaArea(postCard);
        if (!mediaArea) return;

        const boardId = postCard.dataset.boardId || postData?.board_id;
        const attached = Array.isArray(attachedVideos) ? attachedVideos.filter(Boolean) : [];
        const hasImages = Array.isArray(postData?.attached_images) && postData.attached_images.length > 0;
        const hasContentImages = Array.isArray(postData?.content_images) && postData.content_images.length > 0;

        if (attached.length > 0) {
            const videoFilename = String(attached[0] ?? "");
            let videoSrc = "";
            if (boardId) {
                const userId = this.resolveParentUserId(postCard, postData);
                const url = commonUtil.resolvePostVideoUrl(boardId, userId, videoFilename);
                if (url) {
                    videoSrc = url;
                }
            }

            renderParentBoardVideoPreview(mediaArea, {
                videoSrc,
                videoCount: attached.length,
                hasImages: !!this.modalForm.getBoardImagesGrid(mediaArea),
            });
            return;
        }

        if (!hasImages && !hasContentImages) {
            removeParentBoardVideoPreview(mediaArea);
        } else {
            removeParentBoardVideoPreview(mediaArea);
        }
    }

    public updateParentSnsLinks(postCard: HTMLElement, snsLinks: ModalSnsLink[] | null | undefined): void {
        renderParentSnsLinksData(postCard, snsLinks);
    }

    public syncCommentLikeToParent(
        postId: number,
        commentId: number,
        isLiked: boolean,
        likesCount: number,
        postData: ModalPostData | null = null,
    ): void {
        if (!postData) {
            postData = phpPostData(postId) as ModalPostData | null;
        }

        if (!postData) {
            console.warn("[syncCommentLikeToParent] Parent data not found", { postId });
            return;
        }

        if (!postData.comments) {
            postData.comments = {};
        }

        postData.comments[String(commentId)] = {
            is_liked: isLiked,
            likes_count: likesCount,
        };

        const parentCommentBtn = this.modalForm.getParentCommentLikeButton(commentId);

        if (parentCommentBtn) {
            this.likeRender.updateLikeUI(parentCommentBtn, isLiked, likesCount);
        }
    }

    public updateParentTitle(postCard: HTMLElement, newTitle: string): void {
        const titleElement = this.modalForm.getPostCardTitle(postCard);
        if (titleElement) {
            titleElement.textContent = newTitle;
            titleElement.dataset.postTitle = newTitle;
        }

        const titleDataElements = this.modalForm.getTitleDataElements(postCard);
        titleDataElements.forEach((element) => {
            element.dataset.postTitle = newTitle;
        });
    }

    public updateParentContent(postCard: HTMLElement, newContent: string): void {
        const contentElement = this.modalForm.getPostContent(postCard);
        if (!(contentElement instanceof HTMLElement)) return;

        renderParentContentWithHiddenHtml(contentElement, newContent);
    }
}
